import React, { useState, useCallback, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, ChevronLeft, ChevronRight, Menu, Activity, CheckCircle, X, LogOut } from 'lucide-react';
import { cn } from '@/utils/cn';
import { DEFAULT_NAVIGATION } from './navigation.constants';
import { Badge } from '@/components/ui/badge';
import type {
  NavigationSection,
  NavigationItem,
  UserInfo,
  ProcessingStatus,
  SidebarState,
  SidebarContextValue,
  AppSidebarProps,
} from '@/types/sidebar.types';

// ---------- Context ----------
const SidebarContext = createContext<SidebarContextValue | null>(null);
export const useSidebarContext = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebarContext must be used within AppSidebar');
  return ctx;
};

// ---------- Sub-components ----------

const SidebarHeader: React.FC<{
  isCollapsed: boolean;
  onToggle: () => void;
}> = ({ isCollapsed, onToggle }) => (
  <div className="flex items-center justify-between p-4 border-b border-white/15">
    {!isCollapsed && (
      <div className="flex items-center gap-3">
        <Zap className="h-7 w-7 text-cyan-400" />
        <h1 className="text-xl text-white font-bold">ClaimsIQ</h1>
      </div>
    )}
    {isCollapsed && (
      <div className="mx-auto">
        <Zap className="h-7 w-7 text-cyan-400" />
      </div>
    )}
    <button
      onClick={onToggle}
      className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
      aria-label="Toggle navigation sidebar"
    >
      {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
    </button>
  </div>
);

const SidebarUserBadge: React.FC<{ user: UserInfo }> = ({ user }) => (
  <div className="mx-4 mt-4 bg-white/10 rounded-xl p-4 border border-white/15">
    <div className="flex items-center justify-between mb-3">
      <span className="claimsiq-badge claimsiq-badge-professional text-[10px]">
        {user.plan}
      </span>
      <div className="flex items-center gap-1">
        <CheckCircle className="h-3 w-3 text-green-400" />
        <span className="text-xs text-green-400 font-semibold">Active</span>
      </div>
    </div>
    <div className="space-y-1">
      <div className="text-white text-sm font-medium">{user.company}</div>
      <div className="text-white/70 text-xs">Legal Technology Portal</div>
    </div>
  </div>
);

const SidebarItem: React.FC<{
  item: NavigationItem;
  isActive: boolean;
  onClick: () => void;
}> = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      disabled={item.disabled}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex w-full items-center px-6 py-2.5 transition-colors text-sm',
        isActive
          ? 'bg-white/20 text-white font-medium border-l-2 border-cyan-400'
          : 'text-white/70 hover:text-white hover:bg-white/10',
        item.disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <Icon className="h-4 w-4 mr-3 flex-shrink-0" />
      <span className="flex-1 text-left">{item.name}</span>
      {item.badge && (
        <Badge
          variant={item.badge.variant === 'secondary' ? 'secondary' : 'default'}
          className="ml-2 text-[10px] h-5 min-w-[20px] justify-center"
        >
          {item.badge.text}
        </Badge>
      )}
    </button>
  );
};

const SidebarSection: React.FC<{
  section: NavigationSection;
  isExpanded: boolean;
  currentPath: string;
  onToggle: () => void;
  onItemClick: (path: string) => void;
}> = ({ section, isExpanded, currentPath, onToggle, onItemClick }) => {
  const Icon = section.icon;
  return (
    <div className="mt-1">
      <button
        onClick={onToggle}
        className="flex w-full items-center px-4 py-3 text-white/70 hover:text-white hover:bg-white/10 transition-colors rounded-lg mx-2"
        style={{ width: 'calc(100% - 16px)' }}
      >
        <Icon className="h-4 w-4 mr-3" />
        <span className="flex-1 text-sm font-medium text-left">{section.title}</span>
        <ChevronLeft
          className={cn('h-4 w-4 transition-transform', isExpanded ? 'rotate-[-90deg]' : '-rotate-90')}
          style={{ transform: isExpanded ? 'rotate(-90deg)' : 'rotate(0deg)' }}
        />
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="bg-black/10 rounded-lg mx-2 mt-1 py-1">
              {section.items.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  isActive={currentPath === item.path}
                  onClick={() => onItemClick(item.path)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SidebarStatusPanel: React.FC<{ status: ProcessingStatus }> = ({ status }) => (
  <div className="px-4 pb-6 mt-auto">
    <div className="bg-black/20 backdrop-blur-sm rounded-xl p-4 border border-white/10">
      <h4 className="font-semibold text-sm mb-3 text-white/90">Processing Status</h4>
      <div className="space-y-2">
        <div className="flex justify-between items-center text-xs">
          <span className="text-white/70">Active Jobs</span>
          <span className="font-semibold text-cyan-400 flex items-center gap-1">
            {status.isProcessing && <Activity className="h-3 w-3 animate-pulse" />}
            {status.activeJobs}
          </span>
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className="text-white/70">Completed Today</span>
          <span className="font-semibold text-green-400">{status.completedToday}</span>
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className="text-white/70">Available Credits</span>
          <span className="font-semibold text-green-400">{status.availableCredits.toLocaleString()}</span>
        </div>
      </div>
    </div>
  </div>
);

// ---------- Main Component ----------

export const AppSidebar: React.FC<AppSidebarProps> = ({
  currentPath,
  onNavigate,
  defaultCollapsed = false,
  customNavigation,
  user,
  processingStatus,
  className,
  enableAnimations = true,
  variant = 'default',
  onLogout,
}) => {
  const navigation = customNavigation || DEFAULT_NAVIGATION;

  const [state, setState] = useState<SidebarState>({
    isCollapsed: defaultCollapsed,
    expandedSections: navigation.filter((s) => s.defaultExpanded).map((s) => s.id),
    currentPath,
    isMobile: typeof window !== 'undefined' ? window.innerWidth < 1024 : false,
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  // Responsive detection
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      setState((prev) => ({ ...prev, isMobile }));
      if (!isMobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setState((prev) => ({ ...prev, currentPath }));
  }, [currentPath]);

  const actions = {
    toggleCollapsed: useCallback(() => {
      if (state.isMobile) {
        setMobileOpen((prev) => !prev);
      } else {
        setState((prev) => ({ ...prev, isCollapsed: !prev.isCollapsed }));
      }
    }, [state.isMobile]),
    toggleSection: useCallback((sectionId: string) => {
      setState((prev) => ({
        ...prev,
        expandedSections: prev.expandedSections.includes(sectionId)
          ? prev.expandedSections.filter((id) => id !== sectionId)
          : [...prev.expandedSections, sectionId],
      }));
    }, []),
    setCurrentPath: useCallback((path: string) => {
      setState((prev) => ({ ...prev, currentPath: path }));
    }, []),
    expandAll: useCallback(() => {
      setState((prev) => ({ ...prev, expandedSections: navigation.map((s) => s.id) }));
    }, [navigation]),
    collapseAll: useCallback(() => {
      setState((prev) => ({ ...prev, expandedSections: [] }));
    }, []),
  };

  const handleItemClick = useCallback(
    (path: string) => {
      onNavigate(path);
      if (state.isMobile) setMobileOpen(false);
    },
    [onNavigate, state.isMobile]
  );

  const ctxValue: SidebarContextValue = {
    state,
    actions,
    config: { mobileBreakpoint: 1024, enableAnimations, variant },
  };

  const sidebarContent = (
    <motion.aside
      className={cn(
        'flex flex-col h-screen overflow-y-auto claimsiq-gradient-bg',
        state.isCollapsed && !state.isMobile ? 'w-16' : 'w-72',
        className
      )}
      animate={{ width: state.isCollapsed && !state.isMobile ? 64 : 288 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      role="navigation"
      aria-label="Main navigation"
      data-testid="app-sidebar"
    >
      <SidebarHeader isCollapsed={state.isCollapsed && !state.isMobile} onToggle={actions.toggleCollapsed} />

      {user && !(state.isCollapsed && !state.isMobile) && <SidebarUserBadge user={user} />}

      <nav className="flex-1 py-4 overflow-y-auto">
        {!(state.isCollapsed && !state.isMobile) ? (
          navigation.map((section) => (
            <SidebarSection
              key={section.id}
              section={section}
              isExpanded={state.expandedSections.includes(section.id)}
              currentPath={state.currentPath}
              onToggle={() => actions.toggleSection(section.id)}
              onItemClick={handleItemClick}
            />
          ))
        ) : (
          navigation.map((section) => {
            const Icon = section.icon;
            return (
              <div key={section.id} className="py-2 flex justify-center">
                <button
                  className="p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  title={section.title}
                  onClick={() => {
                    actions.toggleCollapsed();
                    actions.toggleSection(section.id);
                  }}
                >
                  <Icon className="h-5 w-5" />
                </button>
              </div>
            );
          })
        )}
      </nav>

      {onLogout && !(state.isCollapsed && !state.isMobile) && (
        <div className="px-4 mb-2">
          <button
            onClick={onLogout}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>
      )}

      {processingStatus && !(state.isCollapsed && !state.isMobile) && (
        <SidebarStatusPanel status={processingStatus} />
      )}
    </motion.aside>
  );

  // Mobile rendering
  if (state.isMobile) {
    return (
      <SidebarContext.Provider value={ctxValue}>
        {/* Mobile toggle button */}
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-4 left-4 z-30 p-2 bg-white rounded-lg shadow-lg lg:hidden"
          aria-label="Open navigation"
        >
          <Menu className="h-6 w-6 text-gray-700" />
        </button>

        {/* Overlay */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setMobileOpen(false)}
              />
              <motion.div
                initial={{ x: -288 }}
                animate={{ x: 0 }}
                exit={{ x: -288 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="fixed top-0 left-0 z-50 h-screen"
              >
                <button
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-4 right-4 z-50 p-1 text-white/70 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
                {sidebarContent}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </SidebarContext.Provider>
    );
  }

  // Desktop rendering
  return (
    <SidebarContext.Provider value={ctxValue}>
      <div className="fixed top-0 left-0 z-30 h-screen">{sidebarContent}</div>
    </SidebarContext.Provider>
  );
};

export default AppSidebar;